/**
 * Consultation des résultats pour la caisse (2026-10-02).
 *
 * Avant d'accepter une réinscription, la caisse vérifie si l'étudiant a validé
 * son année. Recherche par nom ou matricule, puis les DEUX semestres de l'année
 * choisie côte à côte (impair à gauche, pair à droite), années antérieures
 * comprises.
 *
 * Un étudiant redevable reste soumis à la règle du relevé : le serveur répond
 * 403 avec le relevé léger (décision + matières à rattraper, sans notes) et les
 * périodes impayées. On l'affiche tel quel — c'est précisément ce dont la
 * caisse a besoin pour orienter l'étudiant.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Printer, Search, X } from 'lucide-react';
import apiClient from '../../config/api.config';
import { useGetInfoInstitutQuery } from '../../services/coreApi';
import { documentA4, imprimerHtmlDansIframe } from '../../utils/impressionA4';
import { useGetAnneesAcademiquesQuery, useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import type { DonneesReleve, ReleveLegerAgent } from '../../services/relevesApi';

interface EtudiantRecherche {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  classe?: string;
  niveau?: string;
}

type EtatSemestre =
  | { etat: 'chargement' }
  | { etat: 'complet'; data: DonneesReleve }
  | { etat: 'leger'; leger: ReleveLegerAgent; message: string }
  | { etat: 'absent'; message: string }
  | { etat: 'erreur'; message: string };

const fmt = (v: number | null | undefined) =>
  v === null || v === undefined || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(2);

const fmtFcfa = (s: string | null) =>
  s ? `${new Intl.NumberFormat('fr-FR').format(parseFloat(s))} FCFA` : '';

/** Semestre absolu affiché : L1 → S1/S2, L2 → S3/S4, L3 → S5/S6, M1 → S7/S8, M2 → S9/S10. */
const semestreAbsolu = (niveau: string | undefined, local: 1 | 2): string => {
  const rang: Record<string, number> = { L1: 0, L2: 1, L3: 2, M1: 3, M2: 4 };
  const r = rang[(niveau || '').toUpperCase()];
  return r === undefined ? `Semestre ${local}` : `S${r * 2 + local}`;
};

const mention = (moy: number | null | undefined): string => {
  if (moy === null || moy === undefined) return '';
  if (moy >= 16) return 'Très bien';
  if (moy >= 14) return 'Bien';
  if (moy >= 12) return 'Assez bien';
  if (moy >= 10) return 'Passable';
  return '';
};

async function chargerSemestre(matricule: string, semestre: 1 | 2, annee: string): Promise<EtatSemestre> {
  try {
    const { data } = await apiClient.get<DonneesReleve>('/releves/donnees-releve/', {
      params: { matricule, semestre, annee_academique: annee },
      headers: { 'X-Annee-Academique': annee },
    });
    return { etat: 'complet', data };
  } catch (e: any) {
    const status = e?.response?.status;
    const body = e?.response?.data || {};
    if (status === 403 && body.releve_leger) {
      return { etat: 'leger', leger: body.releve_leger as ReleveLegerAgent, message: body.error || '' };
    }
    if (status === 404) {
      return { etat: 'absent', message: `Pas inscrit en ${annee}.` };
    }
    return { etat: 'erreur', message: body.error || body.detail || 'Résultats indisponibles.' };
  }
}

/** Le semestre est-il validé ? null = résultat pas encore disponible. */
const decision = (s: EtatSemestre | undefined): boolean | null => {
  if (!s) return null;
  if (s.etat === 'complet') {
    const rs = s.data.resultat_semestre;
    if (!rs || rs.moyenne_semestre === null) return null;
    return !!rs.valide;
  }
  if (s.etat === 'leger') return s.leger.decision_disponible ? !!s.leger.valide : null;
  return null;
};

const BadgeDecision: React.FC<{ valide: boolean | null; session?: string }> = ({ valide, session }) => {
  if (valide === null) {
    return <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">Résultat non disponible</span>;
  }
  return valide ? (
    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
      Validé{session === 'rattrapage' ? ' (rattrapage)' : session === 'normale' ? ' (session normale)' : ''}
    </span>
  ) : (
    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Non validé</span>
  );
};

const ColonneSemestre: React.FC<{ titre: string; etat: EtatSemestre | undefined }> = ({ titre, etat }) => {
  const entete = (droite?: React.ReactNode) => (
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">{titre}</h3>
      {droite}
    </div>
  );
  const cadre = 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden';

  if (!etat || etat.etat === 'chargement') {
    return <div className={cadre}>{entete()}<div className="p-6 text-sm text-gray-500">Chargement…</div></div>;
  }
  if (etat.etat === 'absent' || etat.etat === 'erreur') {
    return <div className={cadre}>{entete()}<div className="p-6 text-sm text-gray-600 dark:text-gray-300">{etat.message}</div></div>;
  }

  if (etat.etat === 'leger') {
    const l = etat.leger;
    const valide = l.decision_disponible ? !!l.valide : null;
    const ues = l.ues_a_rattraper || [];
    return (
      <div className={cadre}>
        {entete(<BadgeDecision valide={valide} />)}
        <div className="p-4 space-y-3 text-sm">
          <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-amber-900 dark:text-amber-200">
            Paiement de l'année en cours non à jour : les notes ne sont pas affichées, seule la décision l'est.
            {l.periodes_dues.length > 0 && (
              <div className="mt-1 font-semibold">
                {l.periodes_dues.every((d) => !d.present)
                  ? "Aucun paiement enregistré pour l'année en cours."
                  : <>À régulariser :{' '}
                    {l.periodes_dues.map((d) => (d.present && d.solde && parseFloat(d.solde) > 0
                      ? `${d.label} (reste ${fmtFcfa(d.solde)})`
                      : `${d.label} (non enregistré)`)).join(' · ')}</>}
              </div>
            )}
          </div>
          {!l.decision_disponible ? (
            <p className="text-gray-600 dark:text-gray-300">{l.message || 'Résultats non encore disponibles.'}</p>
          ) : l.valide ? (
            <p className="font-semibold text-green-700 dark:text-green-400">Semestre validé — aucune matière à rattraper.</p>
          ) : (
            <div>
              <p className="font-semibold text-red-700 dark:text-red-400 mb-2">Matières à rattraper :</p>
              {ues.map((ue) => (
                <div key={ue.code_ue || ue.nom_ue} className="mb-2">
                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">{ue.nom_ue}</div>
                  <ul className="list-disc list-inside text-gray-800 dark:text-gray-200">
                    {ue.matieres.length > 0
                      ? ue.matieres.map((m) => <li key={m.code_matiere || m.nom_matiere}>{m.nom_matiere}</li>)
                      : <li className="text-gray-500">Toute l'UE</li>}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const d = etat.data;
  const rs = d.resultat_semestre;
  const valide = decision(etat);
  return (
    <div className={cadre}>
      {entete(<BadgeDecision valide={valide} session={rs?.session_validation} />)}
      <div className="grid grid-cols-3 divide-x divide-gray-200 dark:divide-gray-700 border-b border-gray-200 dark:border-gray-700 text-center">
        <div className="p-3">
          <div className="text-xs text-gray-500">Moyenne</div>
          <div className={`text-2xl font-bold ${rs?.moyenne_semestre != null && rs.moyenne_semestre >= 10 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
            {fmt(rs?.moyenne_semestre)}
          </div>
          <div className="text-xs text-gray-500">{mention(rs?.moyenne_semestre)}</div>
        </div>
        <div className="p-3">
          <div className="text-xs text-gray-500">Crédits</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {rs ? `${rs.credits_obtenus}/${rs.credits_requis}` : '—'}
          </div>
        </div>
        <div className="p-3">
          <div className="text-xs text-gray-500">Rang</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {rs?.rang ? `${rs.rang}${rs.effectif_classe ? `/${rs.effectif_classe}` : ''}` : '—'}
          </div>
        </div>
      </div>
      {d.ues.length === 0 ? (
        <div className="p-6 text-sm text-gray-500">Aucune note enregistrée pour ce semestre.</div>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {d.ues.map((ue) => (
              <React.Fragment key={ue.ue_id ?? ue.code_ue}>
                <tr className="bg-gray-50 dark:bg-gray-900/40">
                  <td className="px-3 py-1.5 font-semibold text-gray-800 dark:text-gray-200">{ue.nom_ue}</td>
                  <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">{fmt(ue.moyenne_ue)}</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap text-xs">
                    {ue.validee
                      ? <span className="text-green-700 dark:text-green-400">{ue.validee_par_compensation ? 'Compensée' : 'Validée'}</span>
                      : <span className="text-red-700 dark:text-red-400 font-semibold">Non validée</span>}
                  </td>
                </tr>
                {ue.matieres.map((m) => (
                  <tr key={m.matiere_id} className="border-t border-gray-100 dark:border-gray-700/60">
                    <td className="pl-6 pr-3 py-1 text-gray-700 dark:text-gray-300">
                      {m.nom_matiere}
                      {m.est_rattrapage && <span className="ml-1 text-xs text-gray-500">(R)</span>}
                    </td>
                    <td className={`px-2 py-1 text-right whitespace-nowrap ${m.moyenne_ecue != null && m.moyenne_ecue < 10 ? 'text-red-700 dark:text-red-400 font-semibold' : 'text-gray-800 dark:text-gray-200'}`}>
                      {fmt(m.moyenne_ecue)}
                    </td>
                    <td className="px-3 py-1 text-right text-xs text-gray-500 whitespace-nowrap">{m.credits} cr</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

interface Consultation {
  date: string;
  agent: string;
  matricule: string;
  annee_academique: string;
  statut: 'complet' | 'leger' | 'erreur';
  nom: string;
  prenoms: string;
  classe: string;
}

const esc = (t: string | number | null | undefined) =>
  String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Matières à rattraper d'un semestre, par UE (même règle que le relevé léger). */
const aRattraper = (s: EtatSemestre | undefined): { ue: string; matieres: string[] }[] => {
  if (s?.etat === 'leger') {
    return (s.leger.ues_a_rattraper || []).map((u) => ({ ue: u.nom_ue, matieres: u.matieres.map((m) => m.nom_matiere) }));
  }
  if (s?.etat === 'complet' && s.data.resultat_semestre && !s.data.resultat_semestre.valide) {
    return s.data.ues.filter((u) => !u.validee).map((u) => ({
      ue: u.nom_ue,
      matieres: u.matieres.filter((m) => m.moyenne_ecue != null && m.moyenne_ecue < 10).map((m) => m.nom_matiere),
    }));
  }
  return [];
};

/** Fiche A4 « décision avant réinscription » : document interne de la caisse. */
function ficheDecisionHtml(o: {
  nom: string; prenoms: string; matricule: string; classe: string; option: string;
  annee: string; verdict: string; ville: string; agent: string;
  semestres: { titre: string; etat: EtatSemestre | undefined }[];
}): string {
  const blocs = o.semestres.map(({ titre, etat }) => {
    const valide = decision(etat);
    const libelle = valide === null ? 'Résultat non disponible' : valide ? 'Validé' : 'Non validé';
    let chiffres = '';
    if (etat?.etat === 'complet' && etat.data.resultat_semestre) {
      const rs = etat.data.resultat_semestre;
      chiffres = `Moyenne : <b>${esc(fmt(rs.moyenne_semestre))}</b> &nbsp;·&nbsp; Crédits : <b>${esc(rs.credits_obtenus)}/${esc(rs.credits_requis)}</b>`;
    } else if (etat?.etat === 'leger') {
      chiffres = "Notes non communiquées (paiement de l'année en cours non à jour).";
    } else if (etat?.etat === 'absent' || etat?.etat === 'erreur') {
      chiffres = esc(etat.message);
    }
    const liste = aRattraper(etat);
    const rattrapage = liste.length === 0 ? '' : `<div class="rat"><b>Matières à rattraper :</b><ul>${
      liste.map((u) => `<li>${esc(u.ue)}${u.matieres.length ? ' : ' + u.matieres.map(esc).join(', ') : " (toute l'UE)"}</li>`).join('')
    }</ul></div>`;
    const classe = valide ? 'ok' : valide === false ? 'ko' : '';
    return `<div class="sem"><div class="semt"><span>${esc(titre)}</span><span class="${classe}">${libelle}</span></div>
      <div>${chiffres}</div>${rattrapage}</div>`;
  }).join('');
  const date = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  return documentA4({
    titre: `Fiche_decision_${o.matricule}_${o.annee}`,
    css: `.feuille-a4{padding:16mm 18mm;font-family:Arial,Helvetica,sans-serif;font-size:11pt;color:#111}
      .ent{display:flex;align-items:center;gap:12px;border-bottom:2px solid #111;padding-bottom:8px}
      .ent img{height:56px} h1{font-size:15pt;margin:14px 0 4px} .sous{color:#555;font-size:9.5pt}
      .id td{padding:3px 10px 3px 0} .verdict{margin:14px 0;padding:10px 12px;border:2px solid #111;font-weight:bold;font-size:12.5pt}
      .sem{border:1px solid #999;padding:8px 10px;margin-bottom:10px} .semt{display:flex;justify-content:space-between;font-weight:bold;margin-bottom:4px}
      .ok{color:#166534} .ko{color:#b91c1c} .rat ul{margin:4px 0 0 18px;padding:0} .rat li{margin:2px 0}
      .pied{margin-top:28px;display:flex;justify-content:space-between} .note{margin-top:30px;color:#666;font-size:8.5pt}`,
    corps: `<div class="feuille-a4">
      <div class="ent"><img src="/logo.png" alt=""/><div><b>Service de la caisse</b><div class="sous">Vérification des résultats avant réinscription</div></div></div>
      <h1>Fiche de décision — année ${esc(o.annee)}</h1>
      <table class="id"><tr><td>Nom et prénoms</td><td><b>${esc(o.nom)} ${esc(o.prenoms)}</b></td></tr>
        <tr><td>Matricule</td><td><b>${esc(o.matricule)}</b></td></tr>
        <tr><td>Classe</td><td>${esc(o.classe)}${o.option ? ' — ' + esc(o.option) : ''}</td></tr></table>
      <div class="verdict">${esc(o.verdict)}</div>
      ${blocs}
      <div class="pied"><div>Fait à ${esc(o.ville)}, le ${date}</div><div>Visa de la caisse<br/><br/>${esc(o.agent)}</div></div>
      <div class="note">Document interne de vérification. Il ne remplace pas le relevé de notes officiel.</div>
    </div>`,
  });
}

const ConsultationResultats: React.FC = () => {
  const utilisateur = useSelector((state: any) => state.auth.user);
  const estAdmin = utilisateur?.role === 'super_admin' || utilisateur?.role === 'admin';
  const { data: infoInstitut } = useGetInfoInstitutQuery();
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [impression, setImpression] = useState(false);
  const { data: annees = [] } = useGetAnneesAcademiquesQuery();
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const [annee, setAnnee] = useState('');
  useEffect(() => {
    if (!annee && anneeActive?.libelle) setAnnee(anneeActive.libelle);
  }, [anneeActive?.libelle, annee]);
  const anneesTriees = useMemo(
    () => [...annees].sort((a, b) => b.libelle.localeCompare(a.libelle)),
    [annees],
  );

  const [recherche, setRecherche] = useState('');
  const [suggestions, setSuggestions] = useState<EtudiantRecherche[]>([]);
  const [rechercheEnCours, setRechercheEnCours] = useState(false);
  const [listeOuverte, setListeOuverte] = useState(false);
  const [etudiant, setEtudiant] = useState<EtudiantRecherche | null>(null);
  const [s1, setS1] = useState<EtatSemestre>();
  const [s2, setS2] = useState<EtatSemestre>();
  const champ = useRef<HTMLInputElement>(null);

  // Recherche différée par nom, prénom ou matricule, dans le schéma de l'année choisie.
  useEffect(() => {
    const terme = recherche.trim();
    if (terme.length < 2 || !annee) {
      setSuggestions([]);
      return;
    }
    let obsolete = false;
    setRechercheEnCours(true);
    const t = window.setTimeout(async () => {
      try {
        const { data } = await apiClient.get('/etudiants/', {
          params: { search: terme, page_size: 30 },
          headers: { 'X-Annee-Academique': annee },
        });
        // Sans ?page= l'API renvoie toute la liste : on n'en garde que le début.
        const liste: EtudiantRecherche[] = (Array.isArray(data) ? data : (data?.results || [])).slice(0, 30);
        if (!obsolete) {
          setSuggestions(liste);
          setListeOuverte(true);
        }
      } catch {
        if (!obsolete) setSuggestions([]);
      } finally {
        if (!obsolete) setRechercheEnCours(false);
      }
    }, 300);
    return () => {
      obsolete = true;
      window.clearTimeout(t);
    };
  }, [recherche, annee]);

  const charger = useCallback(async (matricule: string, an: string) => {
    setS1({ etat: 'chargement' });
    setS2({ etat: 'chargement' });
    const [r1, r2] = await Promise.all([
      chargerSemestre(matricule, 1, an),
      chargerSemestre(matricule, 2, an),
    ]);
    setS1(r1);
    setS2(r2);
  }, []);

  // Changer d'année garde l'étudiant affiché : on recharge ses résultats de cette année.
  useEffect(() => {
    if (etudiant && annee) charger(etudiant.matricule, annee);
  }, [etudiant, annee, charger]);

  const choisir = (e: EtudiantRecherche) => {
    setEtudiant(e);
    setRecherche('');
    setSuggestions([]);
    setListeOuverte(false);
  };

  // Historique : chargé à l'ouverture et à chaque retour sur la recherche.
  useEffect(() => {
    if (etudiant) return;
    let obsolete = false;
    apiClient.get('/releves/consultations-caisse/', { params: { jours: 30 } })
      .then(({ data }) => { if (!obsolete) setConsultations(data?.consultations || []); })
      .catch(() => { if (!obsolete) setConsultations([]); });
    return () => { obsolete = true; };
  }, [etudiant]);

  const rouvrir = (c: Consultation) => {
    setAnnee(c.annee_academique);
    setEtudiant({ id: 0, matricule: c.matricule, nom: c.nom, prenoms: c.prenoms, classe: c.classe });
  };

  const effacer = () => {
    setEtudiant(null);
    setS1(undefined);
    setS2(undefined);
    setRecherche('');
    champ.current?.focus();
  };

  // Identité et niveau tels qu'enregistrés dans l'année consultée.
  const identite = (() => {
    for (const s of [s1, s2]) {
      if (s?.etat === 'complet') return s.data.etudiant;
      if (s?.etat === 'leger') return s.leger.etudiant;
    }
    return null;
  })();
  const niveau = identite?.niveau || etudiant?.niveau;

  // Verdict de l'année : validée seulement si les deux semestres le sont.
  const d1 = decision(s1);
  const d2 = decision(s2);
  const pret = s1 && s2 && s1.etat !== 'chargement' && s2.etat !== 'chargement';
  const absentAnnee = s1?.etat === 'absent' && s2?.etat === 'absent';
  const redevable = s1?.etat === 'leger' || s2?.etat === 'leger';
  let verdict: { texte: string; classe: string } | null = null;
  if (pret && !absentAnnee) {
    if (d1 === true && d2 === true) {
      verdict = { texte: `Année ${annee} validée — les deux semestres sont validés.`, classe: 'border-green-400 bg-green-50 text-green-900 dark:bg-green-900/20 dark:text-green-200 dark:border-green-800' };
    } else if (d1 === false || d2 === false) {
      const manque = [d1 === false ? semestreAbsolu(niveau, 1) : '', d2 === false ? semestreAbsolu(niveau, 2) : ''].filter(Boolean).join(' et ');
      verdict = { texte: `Année ${annee} non validée — ${manque} à reprendre.`, classe: 'border-red-400 bg-red-50 text-red-900 dark:bg-red-900/20 dark:text-red-200 dark:border-red-800' };
    } else {
      verdict = { texte: `Année ${annee} : résultats pas encore complets.`, classe: 'border-gray-300 bg-gray-50 text-gray-800 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600' };
    }
  }

  const imprimerFiche = async () => {
    if (!etudiant || !verdict) return;
    setImpression(true);
    try {
      await imprimerHtmlDansIframe(ficheDecisionHtml({
        nom: identite?.nom || etudiant.nom,
        prenoms: identite?.prenoms || etudiant.prenoms,
        matricule: etudiant.matricule,
        classe: identite?.classe || etudiant.classe || '',
        option: identite?.option || '',
        annee,
        verdict: verdict.texte,
        ville: infoInstitut?.ville || '',
        agent: [utilisateur?.first_name, utilisateur?.last_name].filter(Boolean).join(' ') || utilisateur?.username || '',
        semestres: [
          { titre: `${semestreAbsolu(niveau, 1)} — semestre impair`, etat: s1 },
          { titre: `${semestreAbsolu(niveau, 2)} — semestre pair`, etat: s2 },
        ],
      }));
    } finally {
      setImpression(false);
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Résultats des étudiants</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Tapez le nom ou le matricule : les deux semestres de l'année s'affichent côte à côte.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            ref={champ}
            autoFocus
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            onFocus={() => suggestions.length > 0 && setListeOuverte(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && suggestions.length > 0) choisir(suggestions[0]);
              if (e.key === 'Escape') setListeOuverte(false);
            }}
            placeholder="Nom, prénom ou matricule de l'étudiant"
            className="w-full pl-10 pr-3 py-3 text-lg rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {listeOuverte && recherche.trim().length >= 2 && (
            <div className="absolute z-20 mt-1 w-full max-h-96 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
              {rechercheEnCours && suggestions.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500">Recherche…</div>
              ) : suggestions.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500">Aucun étudiant trouvé en {annee} dans les pôles autorisés pour ce compte.</div>
              ) : suggestions.map((e) => (
                <button
                  key={e.id}
                  onClick={() => choisir(e)}
                  className="w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0"
                >
                  <div className="font-semibold text-gray-900 dark:text-gray-100">{e.nom} {e.prenoms}</div>
                  <div className="text-xs text-gray-500">{e.matricule} · {e.classe || '—'}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <select
          value={annee}
          onChange={(e) => setAnnee(e.target.value)}
          className="md:w-56 px-3 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        >
          {anneesTriees.map((a) => (
            <option key={a.id} value={a.libelle}>
              {a.libelle}{a.est_active ? ' (en cours)' : ''}
            </option>
          ))}
        </select>
      </div>

      {etudiant && (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex items-start justify-between gap-3">
            <div>
              <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {identite?.nom || etudiant.nom} {identite?.prenoms || etudiant.prenoms}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                {etudiant.matricule} · {identite?.classe || etudiant.classe || '—'}
                {identite?.option ? ` · ${identite.option}` : ''} · {annee}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={imprimerFiche}
                disabled={!pret || absentAnnee || impression}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-gray-900 text-white hover:bg-gray-700 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-300 disabled:opacity-40"
              >
                <Printer size={14} /> {impression ? 'Préparation…' : 'Imprimer la fiche'}
              </button>
              <button onClick={effacer} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                <X size={14} /> Autre étudiant
              </button>
            </div>
          </div>

          {absentAnnee ? (
            <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-4 text-gray-700 dark:text-gray-200">
              Cet étudiant n'était pas inscrit en {annee}. Choisissez une autre année.
            </div>
          ) : (
            <>
              {verdict && (
                <div className={`rounded-lg border-2 p-4 text-lg font-semibold ${verdict.classe}`}>
                  {verdict.texte}
                  {redevable && (
                    <div className="text-sm font-normal mt-1">
                      Paiement de l'année en cours non à jour : seules les décisions sont visibles, sans les notes.
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
                <ColonneSemestre titre={`${semestreAbsolu(niveau, 1)} — semestre impair`} etat={s1} />
                <ColonneSemestre titre={`${semestreAbsolu(niveau, 2)} — semestre pair`} etat={s2} />
              </div>
            </>
          )}
        </>
      )}

      {!etudiant && consultations.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Consultations récentes {estAdmin ? 'de la caisse ' : ''}— 30 derniers jours
            </h2>
            <p className="text-xs text-gray-500">Cliquez sur une ligne pour rouvrir les résultats.</p>
          </div>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs text-gray-500 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Date</th>
                  <th className="text-left px-2 py-2 font-medium">Étudiant</th>
                  <th className="text-left px-2 py-2 font-medium">Classe</th>
                  <th className="text-left px-2 py-2 font-medium">Année</th>
                  {estAdmin && <th className="text-left px-2 py-2 font-medium">Agent</th>}
                  <th className="text-left px-4 py-2 font-medium">Vue</th>
                </tr>
              </thead>
              <tbody>
                {consultations.map((c, i) => (
                  <tr
                    key={`${c.date}-${c.matricule}-${i}`}
                    onClick={() => rouvrir(c)}
                    className="border-t border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700/60"
                  >
                    <td className="px-4 py-1.5 whitespace-nowrap text-gray-600 dark:text-gray-400">
                      {new Date(c.date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">
                      {c.nom ? `${c.nom} ${c.prenoms}` : c.matricule}
                      <span className="text-xs text-gray-500"> · {c.matricule}</span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">{c.classe || '—'}</td>
                    <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">{c.annee_academique}</td>
                    {estAdmin && <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">{c.agent}</td>}
                    <td className="px-4 py-1.5 text-xs text-gray-500">
                      {c.statut === 'complet' ? 'Complète' : c.statut === 'leger' ? 'Décision seule' : 'Erreur'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationResultats;
